import React, { useState } from 'react';
import { message, Breadcrumb, Button, Space, Select, Form, DatePicker } from 'antd';
import { DashboardOutlined, PlusSquareOutlined, SwapOutlined } from '@ant-design/icons';
import TransactionTable from '../../components/store/TransactionTable';
import TransactionModal from '../../components/store/TransactionModal';
import TransactionDetailModal from '../../components/store/TransactionDetailModal';
import { 
  useGetAllTransactions, 
  useGetTransactionsByStore, 
  useGetTransactionsByDepartment,
  useGetTransactionsByItem,
  useCreateTransaction 
} from '../../hooks/store/transactionQuery';
import { useGetAllStores } from '../../hooks/store/storeQuery';
import { useGetAllStoreDepartments } from '../../hooks/store/storeDepartmentQuery';
import { searchItems } from '../../hooks/store/itemQuery';
import Header from '../../components/dashboard/header';
import { Link } from 'react-router-dom';

const { Option } = Select;
const { RangePicker } = DatePicker;

const StoreTransactionsPage = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterValue, setFilterValue] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [items, setItems] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const createTransaction = useCreateTransaction();
  
  const { stores } = useGetAllStores();
  const { departments } = useGetAllStoreDepartments(selectedStore, refreshFlag);
  
  const allTransactions = useGetAllTransactions(refreshFlag);
  const storeTransactions = useGetTransactionsByStore(filterType === 'store' && filterValue ? filterValue : null);
  const departmentTransactions = useGetTransactionsByDepartment(filterType === 'department' && filterValue ? filterValue : null);
  const itemTransactions = useGetTransactionsByItem(filterType === 'item' && filterValue ? filterValue : null);

  const getActiveData = () => {
    switch (filterType) {
      case 'store':
        return storeTransactions;
      case 'department':
        return departmentTransactions;
      case 'item':
        return itemTransactions;
      default:
        return allTransactions;
    }
  };

  const { transactions, loading } = getActiveData();

  const handleAdd = () => {
    setModalVisible(true);
  };

  const handleView = (record) => {
    setSelectedTransaction(record);
    setDetailModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      await createTransaction(values);
      message.success('Transaction created successfully');
      setModalVisible(false);
      setRefreshFlag(!refreshFlag);
    } catch (error) {
      message.error('Failed to create transaction');
    }
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
    setFilterValue(null);
    setSelectedStore(null);
  };

  const handleStoreChange = (value) => {
    setSelectedStore(value);
    if (filterType === 'store') {
      setFilterValue(value);
    }
  };

  const handleDepartmentChange = (value) => {
    if (filterType === 'department') {
      setFilterValue(value);
    }
  };

  const handleItemSearch = async (value) => {
    if (value && value.length >= 1) { // Allow search with just 1 character
      setSearchLoading(true);
      try {
        const data = await searchItems(value, 0, 20);
        if (data && data.content) {
          setItems(data.content);
        }
      } catch (error) {
        message.error('Failed to search items');
      }
      setSearchLoading(false);
    } else {
      setItems([]);
    }
  };

  const handleItemChange = (value) => {
    if (filterType === 'item') {
      setFilterValue(value);
    }
  };

  return (
    <>
      <div className="main-container">
        <Header />
        <div className="main-body">
          <div className="info-bar">
            <Breadcrumb>
              <Breadcrumb.Item>
                <DashboardOutlined className="dashboard-icon" />
                <Link to={"/dashboard"}>Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <SwapOutlined />
                <Link to={"/dashboard/store-transactions"}>Transactions</Link>
              </Breadcrumb.Item>
            </Breadcrumb>
            <div style={{ display: 'flex', gap: '1em' }}>
              <Button
                type="primary"
                style={{ backgroundColor: "green", color: "#FFFFFF" }}
                onClick={handleAdd}
                icon={<PlusSquareOutlined />}
              >
                New Transaction
              </Button>
            </div>
          </div>
          
          <div style={{ padding: '20px', background: '#fff', marginBottom: '20px' }}>
            <Form layout="inline">
              <Form.Item label="Filter By">
                <Select
                  value={filterType}
                  onChange={handleFilterTypeChange}
                  style={{ width: 150 }}
                >
                  <Option value="all">All Transactions</Option>
                  <Option value="store">By Store</Option>
                  <Option value="department">By Department</Option>
                  <Option value="item">By Item</Option>
                </Select>
              </Form.Item>

              {filterType === 'store' && (
                <Form.Item label="Store">
                  <Select
                    placeholder="Select store"
                    style={{ width: 200 }}
                    onChange={handleStoreChange}
                    allowClear
                  >
                    {stores.map(store => (
                      <Option key={store.id} value={store.id}>
                        {store.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {filterType === 'department' && (
                <>
                  <Form.Item label="Store">
                    <Select
                      placeholder="Select store first"
                      style={{ width: 200 }}
                      onChange={handleStoreChange}
                      value={selectedStore}
                      allowClear
                    >
                      {stores.map(store => (
                        <Option key={store.id} value={store.id}>
                          {store.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label="Department">
                    <Select
                      placeholder={selectedStore ? "Select department" : "Select store first"}
                      style={{ width: 200 }}
                      onChange={handleDepartmentChange}
                      disabled={!selectedStore}
                      allowClear
                    >
                      {departments.map(dept => (
                        <Option key={dept.id} value={dept.id}>
                          {dept.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              )}

              {filterType === 'item' && (
                <Form.Item label="Item">
                  <Select
                    showSearch
                    placeholder="Search for item"
                    style={{ width: 300 }}
                    onSearch={handleItemSearch}
                    onChange={handleItemChange}
                    filterOption={false}
                    loading={searchLoading}
                    notFoundContent={searchLoading ? 'Searching...' : 'Type to search'}
                    allowClear
                  >
                    {items.map(item => (
                      <Option key={item.id} value={item.id}>
                        {item.name} - {item.storeDepartmentName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Form>
          </div>

          <div className="table-section">
            <TransactionTable
              transactions={transactions}
              onView={handleView}
              loading={loading}
            />
          </div>
        </div>
      </div>
      
      <TransactionModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleSubmit}
        title="Create Transaction"
      />
      
      <TransactionDetailModal
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        transaction={selectedTransaction}
      />
    </>
  );
};

export default StoreTransactionsPage;